import React, { useState, useEffect, useMemo } from 'react';
import { RotateCcw, Loader2, Info } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { adminApi } from '@/lib/api';
import { toast } from 'sonner';

const MIN_LEN = 20;

const ReopenReportDialog = ({ open, onOpenChange, report, onReopened }) => {
  const [reason, setReason] = useState('');
  const [notifyReporter, setNotifyReporter] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setReason('');
      setNotifyReporter(true);
    }
  }, [open]);

  const remaining = useMemo(() => Math.max(0, MIN_LEN - reason.trim().length), [reason]);
  const canSubmit = remaining === 0 && !saving;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      await adminApi.reopenReport(report.id, reason.trim(), notifyReporter);
      toast.success('Denúncia reaberta', {
        description: 'A investigação volta a estar activa. O denunciante foi notificado.',
      });
      onReopened?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao reabrir denúncia');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="reopen-report-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RotateCcw className="w-5 h-5 text-blue-600" />
            Reabrir denúncia
          </DialogTitle>
          <DialogDescription>
            Muitos enquadramentos legais (Lei 93/2021, RGPD) exigem que a reabertura seja fundamentada e auditável.
          </DialogDescription>
        </DialogHeader>

        <Alert className="bg-blue-50 border-blue-200">
          <Info className="w-4 h-4 text-blue-600" />
          <AlertDescription className="text-sm text-blue-900">
            Ao reabrir: <strong>estado volta a EM_INVESTIGAÇÃO</strong>, a data de fecho é removida e o motivo fica no histórico permanente (audit log).
          </AlertDescription>
        </Alert>

        <div>
          <Label className="text-sm font-medium">Motivo da reabertura</Label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ex.: Novos factos apresentados pelo denunciante — dossier 2026/03. Testemunha adicional identificada."
            className="mt-1 min-h-[120px]"
            data-testid="reopen-reason-textarea"
          />
          <div className="flex items-center justify-between mt-1">
            <span className="text-xs text-slate-500">
              {remaining > 0
                ? `Faltam ${remaining} caracteres (mínimo ${MIN_LEN}).`
                : `${reason.trim().length} caracteres — pronto.`}
            </span>
            {remaining === 0 && <span className="text-xs text-emerald-600 font-medium">✓ válido</span>}
          </div>
        </div>

        <div className="flex items-center justify-between rounded-md border p-3">
          <div>
            <p className="text-sm font-medium text-slate-800">Notificar denunciante por email</p>
            <p className="text-xs text-slate-500">Envia mensagem automática com o motivo da reabertura.</p>
          </div>
          <Switch
            checked={notifyReporter}
            onCheckedChange={setNotifyReporter}
            data-testid="reopen-notify-switch"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="confirm-reopen-btn"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Reabrir denúncia
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReopenReportDialog;
